import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../utils/api';
import { ArrowLeft, CheckCircle, Clock, MapPin, Calendar, FileText, BookOpen } from 'lucide-react';

export default function CustomerProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    if (!token) { navigate('/customer/login'); return; }
    api.customerGetProject(projectId, token)
      .then(res => setData(res.data))
      .catch(() => navigate('/customer/portal'))
      .finally(() => setLoading(false));
  }, [projectId, navigate]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-[#0a0a0a]">
      <div className="w-8 h-8 border-2 border-[#E8613A] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  if (!data) return null;
  const { project, total_hours_worked, signatures, daybooks, eta_reports, customer_name } = data;
  const progress = project.progress_percent || 0;

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0a0a0a]" data-testid="customer-project-detail">
      <header className="bg-white dark:bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/customer/portal" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft size={14} /> Tillbaka
          </Link>
          <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <span className="text-[#E8613A]">Operio</span>
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Project header */}
        <div className="bg-white dark:bg-card border border-border rounded-xl p-6">
          <h1 className="text-2xl font-bold text-foreground mb-1">{project.name}</h1>
          {project.description && <p className="text-sm text-muted-foreground mb-4">{project.description}</p>}
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium">Progress</span>
            <span className="font-bold">{progress}%</span>
          </div>
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-[#E8613A] rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Arbetade timmar" value={total_hours_worked?.toFixed(1) || '0'} />
          <StatCard label="Beräknade dagar" value={project.estimated_days || '—'} />
          <StatCard label="ÄTA-rapporter" value={eta_reports?.length || 0} />
          <StatCard label="Signaturer" value={signatures?.length || 0} />
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold mb-3">Projektinfo</h3>
            <dl className="space-y-2 text-sm">
              {project.customer && <Row label="Kund" value={project.customer} />}
              {project.location && <Row label="Plats" value={project.location} />}
              {project.start_date && <Row label="Start" value={project.start_date} />}
              {project.end_date && <Row label="Deadline" value={project.end_date} />}
              {project.rot_eligible && <Row label="ROT-avdrag" value="Ja" />}
            </dl>
          </div>

          <div className="bg-white dark:bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5"><CheckCircle size={14} className="text-green-600" /> Signaturer</h3>
            {signatures?.length === 0 ? (
              <p className="text-sm text-muted-foreground">Inga signaturer ännu</p>
            ) : (
              <div className="space-y-2">
                {signatures.map(s => (
                  <div key={s.id} className="flex items-center gap-2 text-sm">
                    <CheckCircle size={12} className="text-green-600" />
                    <span className="font-medium">{s.signer_name}</span>
                    <span className="text-muted-foreground">— {s.action}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ÄTA Reports */}
        {eta_reports?.length > 0 && (
          <div className="bg-white dark:bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5"><FileText size={14} className="text-[#E8613A]" /> Tilläggsarbeten (ÄTA)</h3>
            <div className="space-y-2">
              {eta_reports.map((eta, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm font-medium">{eta.title}</p>
                    <p className="text-xs text-muted-foreground">{eta.date} • {eta.status}</p>
                  </div>
                  {eta.total_amount > 0 && <span className="text-sm font-bold">{eta.total_amount.toLocaleString('sv-SE')} kr</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daybooks */}
        {daybooks?.length > 0 && (
          <div className="bg-white dark:bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5"><BookOpen size={14} className="text-[#E8613A]" /> Dagbok</h3>
            <div className="space-y-3">
              {daybooks.map((db, i) => (
                <div key={i} className="border-b border-border last:border-0 pb-3 last:pb-0">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{db.date}</p>
                  <p className="text-sm text-foreground whitespace-pre-line">{db.content?.slice(0, 300)}{db.content?.length > 300 ? '...' : ''}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white dark:bg-card border border-border rounded-xl p-5">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
